import { useCallback, useEffect, useRef, useState } from "react";
import { MAX_POLLS, POLL_MS, pollDelay } from "../../../../utils/perfPolling";
import { fetchAthletePerformance } from "./athletePerformanceServices";

// The athlete Performance section's data (spec B13, B14 A), loaded lazily:
//
//   const perf = useAthletePerformance({ athleteId, email, enabled });
//
// - Nothing is asked until `enabled` holds (the profile has the new cards,
//   performanceV2, and the Performance tab is open). Each answer is kept
//   under its own `subject|season` key for as long as the page is mounted,
//   so a tab switch or a season picked again never asks twice, and a late
//   answer for another athlete never shows on this one.
// - It never throws: a failed request, or a server that keeps answering
//   202 (still building) past MAX_POLLS polls, becomes an unavailable
//   section ({ available: false, reason }). Polls follow the answer's
//   retryAfter (4 s by default; utils/perfPolling, the coach team page's
//   cadence too).
// - A failure that may pass (the feed was down, the build outlasted the
//   polls) is asked again by retry() (the section's Try again), and is
//   forgotten when the tab closes, so opening it again asks again too; any
//   other answer is kept.
// - A ready default-season answer whose peer group is still being built
//   (peer.building, spec D4 b: the server started the group for this view)
//   is asked again silently after the answer's retryAfter (4 s by default),
//   at most MAX_REASKS times. The answer on screen stays (the archetype
//   card's shell says the ranks are coming); a new answer for the same
//   season that no longer waits for its group (ranked, or a group that
//   cannot come) replaces it. A re-ask that fails or still waits keeps it.
//   Only the default season's answer is asked again: the cards that show
//   ranks stay on it, while Game by Game, which follows a pick, shows none.
// - No Redux: two suites render ProfileBody with no Provider.
//
// It returns
//   status    "loading" | "building" | "ready" | "unavailable" | "idle"
//   envelope  the endpoint's answer for the season on screen (ready only)
//   baseEnvelope the default season's answer (ready only): the section's
//             other cards stay on it while Game by Game shows a picked
//             season, as the mock's season selector swaps Game by Game only
//   reason    why the section is unavailable (unavailable only)
//   seasonKey the season the selector shows (the one asked for while it loads)
//   pending   { key, building } while a season picked in Game by Game loads
//   notice    { key, reason } when a picked season had nothing to show; the
//             section stays on the season it showed
//   select(key) asks for another season (the default season's key asks
//             for the default answer again)
//   retry()   asks again for the season on screen after a failure that may
//             pass (unavailable with reason feed_error or building)

export { MAX_POLLS, POLL_MS };

// How many times a ready answer whose peer group is being built is asked
// again (D4 b: every 4 s, at most 3 times).
export const MAX_REASKS = 3;

const FEED_ERROR = Object.freeze({ available: false, reason: "feed_error" });
const STILL_BUILDING = Object.freeze({ available: false, reason: "building" });
// A request the server turned down (signed out, not found): the profile's
// own cards, without the league-feed outage note.
const REQUEST_FAILED = Object.freeze({ available: false, reason: "request_failed" });
const OBJECT_ID = /^[a-f0-9]{24}$/i;

// A rejected request as an answer: a 4xx (other than 429) is the request,
// anything else (5xx, 429, no network) the feed or the server.
const failureOf = (err) => {
  const code = Number(err?.status);
  return code >= 400 && code < 500 && code !== 429 ? REQUEST_FAILED : FEED_ERROR;
};

// Who the section is about: the database id, else the email.
export const performanceSubject = (athleteId, email) => {
  const id = String(athleteId ?? "").trim();
  if (OBJECT_ID.test(id)) return id.toLowerCase();
  return String(email ?? "").trim().toLowerCase();
};

const keyOf = (subject, season) => `${subject}|${season || ""}`;

const isBuilding = (res) => res?.status === 202 || res?.data?.status === "building";

const envelopeOf = (res) => (res && res.data && typeof res.data === "object" && !Array.isArray(res.data) ? res.data : FEED_ERROR);

// A season asked for by key is shown only when the answer is that season
// (the server answers its default season for a key it has no rows for).
const sameSeason = (envelope, season) => !season || String(envelope?.season?.key ?? "") === season;

const shows = (answer, season) => answer?.phase === "done" && answer.envelope?.available === true && sameSeason(answer.envelope, season);

// Reasons a later visit may clear: the feed was down, the build ran past
// the polls. A refused request or an athlete with no cards stays as it is.
const PASSING = new Set([FEED_ERROR.reason, STILL_BUILDING.reason]);
const passingFailure = (answer) => answer?.phase === "done" && answer.envelope?.available !== true && PASSING.has(answer.envelope?.reason);

// A ready answer waiting for a peer group being built: its season's (the
// envelope's peer, else the payload's, as the cards read it), or the rank
// season's that will rank its cards (payload.rank_season.peer, D4 d).
const isObject = (v) => Boolean(v) && typeof v === "object";
export const peerBuilding = (envelope) => {
  if (!isObject(envelope) || envelope.available !== true) return false;
  const peer = envelope.peer || envelope.payload?.peer;
  if (isObject(peer) && peer.building === true) return true;
  const rank = [envelope.payload?.rank_season, envelope.payload?.rankSeason, envelope.rank_season, envelope.rankSeason].find(isObject);
  return Boolean(rank && isObject(rank.peer) && rank.peer.building === true && rank.peer.ranked !== true);
};

const seasonKeyOf = (envelope) => String(envelope?.season?.key ?? "");

const useAthletePerformance = ({ athleteId, email, enabled = true } = {}) => {
  const subject = performanceSubject(athleteId, email);
  const [answers, setAnswers] = useState({});
  // Bumped by retry(), so the request effect asks again for a key it forgot.
  const [retries, setRetries] = useState(0);
  const [selection, setSelection] = useState({ subject, wanted: "", shown: "", notice: null });
  const view = selection.subject === subject ? selection : { subject, wanted: "", shown: "", notice: null };
  const live = useRef(true);
  const asked = useRef(new Set());
  const target = useRef({ athleteId, email, subject });
  target.current = { athleteId, email, subject };
  const answersNow = useRef(answers);
  answersNow.current = answers;
  // The silent re-asks of an answer whose peer group is being built: how
  // many each key has sent, the keys with one scheduled or in flight, and
  // their timers (cleared when the page goes).
  const reasked = useRef({});
  const reasking = useRef(new Set());
  const timers = useRef(new Set());

  useEffect(() => {
    live.current = true;
    const pending = timers.current;
    const scheduled = reasking.current;
    return () => {
      live.current = false;
      pending.forEach((t) => clearTimeout(t));
      pending.clear();
      scheduled.clear();
    };
  }, []);

  // The tab closed: forget the failures that may pass, so the next visit
  // asks again.
  useEffect(() => {
    if (enabled) return;
    const failed = Object.keys(answersNow.current).filter((key) => passingFailure(answersNow.current[key]));
    if (!failed.length) return;
    failed.forEach((key) => asked.current.delete(key));
    setAnswers((prev) => {
      const next = { ...prev };
      failed.forEach((key) => delete next[key]);
      return next;
    });
  }, [enabled]);

  const store = useCallback((key, answer) => {
    if (!live.current) return;
    setAnswers((prev) => ({ ...prev, [key]: answer }));
  }, []);

  const request = useCallback(
    (who, season, attempt) => {
      const key = keyOf(who.subject, season);
      let call;
      try {
        call = fetchAthletePerformance({ athleteId: who.athleteId, email: who.email, season });
      } catch (err) {
        call = Promise.reject(err);
      }
      Promise.resolve(call)
        .then((res) => {
          if (!live.current) return;
          if (!isBuilding(res)) {
            store(key, { phase: "done", envelope: envelopeOf(res) });
            return;
          }
          if (attempt >= MAX_POLLS) {
            store(key, { phase: "done", envelope: STILL_BUILDING });
            return;
          }
          store(key, { phase: "building", polls: attempt });
          setTimeout(() => {
            if (!live.current) return;
            if (target.current.subject !== who.subject) {
              // Another athlete is on screen: ask again if this one returns.
              asked.current.delete(key);
              return;
            }
            request(who, season, attempt + 1);
          }, pollDelay(res?.data?.retryAfter));
        })
        .catch((err) => store(key, { phase: "done", envelope: failureOf(err) }));
    },
    [store],
  );

  // Ask for the season wanted, once per key, while enabled (again after
  // retry() forgot it: `retries`).
  useEffect(() => {
    if (!enabled || !subject) return;
    const key = keyOf(subject, view.wanted);
    if (asked.current.has(key)) return;
    asked.current.add(key);
    store(key, { phase: "loading" });
    request(target.current, view.wanted, 0);
  }, [enabled, subject, view.wanted, request, store, retries]);

  // One silent re-ask of the default season (`key`) after `delay`: its new
  // answer replaces the one shown once it no longer waits for its peer
  // group; otherwise the next one follows while re-asks remain.
  const reask = useCallback(
    (who, key, delay) => {
      reasking.current.add(key);
      const timer = setTimeout(() => {
        timers.current.delete(timer);
        if (!live.current) return;
        if (target.current.subject !== who.subject) {
          // Another athlete is on screen: picked up again if this one returns.
          reasking.current.delete(key);
          return;
        }
        reasked.current[key] = (reasked.current[key] || 0) + 1;
        const settle = (next) => {
          if (!live.current) return;
          const held = answersNow.current[key]?.envelope;
          if (next && next.available === true && !peerBuilding(next) && seasonKeyOf(next) === seasonKeyOf(held)) {
            reasking.current.delete(key);
            store(key, { phase: "done", envelope: next });
            return;
          }
          if (reasked.current[key] < MAX_REASKS && target.current.subject === who.subject) {
            reask(who, key, pollDelay(next?.retryAfter));
            return;
          }
          reasking.current.delete(key);
        };
        let call;
        try {
          call = fetchAthletePerformance({ athleteId: who.athleteId, email: who.email, season: "" });
        } catch (err) {
          call = Promise.reject(err);
        }
        Promise.resolve(call)
          .then((res) => settle(isBuilding(res) ? null : envelopeOf(res)))
          .catch(() => settle(null));
      }, delay);
      timers.current.add(timer);
    },
    [store],
  );

  // The default season's answer is in and its peer group is being built:
  // start the re-asks (once per answer's key, MAX_REASKS in all).
  const baseKey = keyOf(subject, "");
  const baseHeld = answers[baseKey];
  useEffect(() => {
    if (!enabled || !subject) return;
    if (baseHeld?.phase !== "done" || !peerBuilding(baseHeld.envelope)) return;
    if (reasking.current.has(baseKey) || (reasked.current[baseKey] || 0) >= MAX_REASKS) return;
    reask(target.current, baseKey, pollDelay(baseHeld.envelope?.retryAfter));
  }, [enabled, subject, baseKey, baseHeld, reask]);

  // A picked season shows once it answers with cards; otherwise the
  // section stays on the season it showed and says why.
  useEffect(() => {
    if (view.wanted === view.shown) return;
    const answer = answers[keyOf(subject, view.wanted)];
    if (!answer || answer.phase !== "done") return;
    if (shows(answer, view.wanted)) {
      setSelection({ subject, wanted: view.wanted, shown: view.wanted, notice: null });
    } else {
      const reason = answer.envelope?.available ? "no_season_data" : answer.envelope?.reason || "feed_error";
      setSelection({ subject, wanted: view.shown, shown: view.shown, notice: { key: view.wanted, reason } });
    }
  }, [answers, subject, view.wanted, view.shown]);

  const defaultKey = String(answers[keyOf(subject, "")]?.envelope?.season?.key ?? "");

  const select = (season) => {
    const picked = String(season ?? "").trim();
    const wanted = !picked || picked === defaultKey ? "" : picked;
    const key = keyOf(subject, wanted);
    const cached = answers[key];
    // A season that had nothing to show is asked again (a feed outage may
    // have passed).
    if (cached?.phase === "done" && !shows(cached, wanted)) {
      asked.current.delete(key);
      setAnswers((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
    setSelection({ subject, wanted, shown: view.shown, notice: null });
  };

  const shownAnswer = answers[keyOf(subject, view.shown)];

  // Try again: forget the shown season's failure that may pass and ask
  // anew (nothing while it loads, is ready, or failed for good).
  const retry = () => {
    if (!passingFailure(shownAnswer)) return;
    const key = keyOf(subject, view.shown);
    asked.current.delete(key);
    setAnswers((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setRetries((n) => n + 1);
  };

  const switching = view.wanted !== view.shown;
  const wantedAnswer = switching ? answers[keyOf(subject, view.wanted)] : null;

  let status = "idle";
  if (!subject) status = "unavailable";
  else if (shownAnswer?.phase === "done") status = shownAnswer.envelope?.available === true ? "ready" : "unavailable";
  else if (shownAnswer?.phase === "building") status = "building";
  else if (shownAnswer || enabled) status = "loading";

  const envelope = status === "ready" ? shownAnswer.envelope : null;
  const baseAnswer = answers[keyOf(subject, "")];
  const base = shows(baseAnswer, "") ? baseAnswer.envelope : null;
  return {
    status,
    envelope,
    baseEnvelope: status === "ready" ? base || envelope : null,
    reason: status === "unavailable" ? (subject ? shownAnswer?.envelope?.reason || "feed_error" : "no_record") : null,
    seasonKey: switching ? view.wanted || defaultKey : String(envelope?.season?.key ?? ""),
    pending: switching ? { key: view.wanted || defaultKey, building: wantedAnswer?.phase === "building" } : null,
    notice: view.notice,
    select,
    retry,
  };
};

export default useAthletePerformance;
